/**
 * Why athletes choose Tymewear — accordion + timed advance (Figma 12443:2113–2198).
 * Mobile (≤749px): horizontal scroll-snap carousel + pill dots (BTB-style).
 */
class WhyAthletesChoose extends HTMLElement {
  constructor() {
    super();
    this._timer = null;
    this._tickTimer = null;
    this._resizeTimer = null;
    this._active = 0;
    this._elapsedMs = 0;
    this._reducedMotion = false;
    this._durationMs = 7000;
    this._tickMs = 50;
    this._onResize = this._onResize.bind(this);
    this._onLoadEqualize = this._onLoadEqualize.bind(this);
    this._onMobileScroll = this._onMobileScroll.bind(this);
    this._mobileAbortController = null;
    this._mobileTrack = null;
    this._mobileDots = [];
    this._mobileScrollRaf = null;
  }

  _isMobileViewport() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('[data-wac-item]'));
    this.fills = Array.from(this.querySelectorAll('[data-wac-fill]'));
    this.panels = Array.from(this.querySelectorAll('[data-wac-panel]'));
    this.buttons = Array.from(this.querySelectorAll('[data-wac-trigger]'));
    this.bodies = this.items.map((row) => row.querySelector('[data-wac-body]')).filter(Boolean);

    const raw = this.getAttribute('data-autoplay-ms');
    const parsed = raw ? parseInt(raw, 10) : NaN;
    this._durationMs = Number.isFinite(parsed) && parsed > 0 ? parsed : 7000;

    this._reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.buttons.forEach((btn, i) => {
      btn.addEventListener('click', () => this.select(i, true));
    });

    this._mobileTrack = this.querySelector('.wac__mobileTrack');
    this._mobileDots = Array.from(this.querySelectorAll('[data-wac-mobile-dot]'));

    if (this._mobileAbortController) {
      this._mobileAbortController.abort();
    }
    this._mobileAbortController = new AbortController();
    const { signal } = this._mobileAbortController;

    if (this._mobileTrack) {
      this._mobileTrack.addEventListener('scroll', this._onMobileScroll, { passive: true, signal });
    }

    this._mobileDots.forEach((dot) => {
      dot.addEventListener(
        'click',
        () => {
          const idx = parseInt(dot.getAttribute('data-wac-mobile-dot'), 10);
          if (!Number.isFinite(idx)) return;
          this.select(idx, true);
        },
        { signal }
      );
    });

    this.select(0, false);

    if (!this._reducedMotion && this.items.length > 1 && !this._isMobileViewport()) {
      this._startCycle();
    }

    window.addEventListener('resize', this._onResize);
    if (document.readyState === 'complete') {
      requestAnimationFrame(() => this._equalizeSpotlightHeights());
    } else {
      window.addEventListener('load', this._onLoadEqualize, { once: true });
    }
    requestAnimationFrame(() => {
      this._equalizeSpotlightHeights();
      if (this._isMobileViewport() && this._mobileTrack) {
        this._scrollMobileToIndex(this._active, false);
        this._setMobileDotActive(this._active);
      }
    });
  }

  _onMobileScroll() {
    if (!this._isMobileViewport() || !this._mobileTrack) return;
    if (this._mobileScrollRaf != null) return;
    this._mobileScrollRaf = requestAnimationFrame(() => {
      this._mobileScrollRaf = null;
      const idx = this._getMobileClosestIndex();
      if (idx !== this._active) {
        this.select(idx, false, { syncMobileScroll: false });
      } else {
        this._setMobileDotActive(idx);
      }
    });
  }

  _getMobileClosestIndex() {
    if (!this._mobileTrack || !this.items.length) return 0;
    const stateWidth = this._mobileTrack.clientWidth || 1;
    const n = this.items.length;
    const idx = Math.round(this._mobileTrack.scrollLeft / stateWidth);
    return Math.max(0, Math.min(n - 1, idx));
  }

  _scrollMobileToIndex(index, smooth) {
    if (!this._mobileTrack) return;
    const stateWidth = this._mobileTrack.clientWidth || 1;
    const target = index * stateWidth;
    const behavior = smooth && !this._reducedMotion ? 'smooth' : 'auto';
    this._mobileTrack.scrollTo({ left: target, behavior });
  }

  _setMobileDotActive(index) {
    this._mobileDots.forEach((dot, j) => {
      const on = j === index;
      dot.classList.toggle('is-active', on);
      dot.setAttribute('aria-current', on ? 'true' : 'false');
    });
  }

  _onLoadEqualize() {
    requestAnimationFrame(() => this._equalizeSpotlightHeights());
  }

  _onResize() {
    clearTimeout(this._resizeTimer);
    this._resizeTimer = setTimeout(() => {
      if (this._isMobileViewport()) {
        this._clearTimers();
      } else if (!this._reducedMotion && this.items.length > 1 && !this._tickTimer) {
        this._startCycle();
      }
      this._equalizeSpotlightHeights();
      if (this._isMobileViewport() && this._mobileTrack) {
        this._scrollMobileToIndex(this._active, false);
        this._setMobileDotActive(this._active);
      }
    }, 120);
  }

  _equalizeSpotlightHeights() {
    if (this._isMobileViewport()) {
      const wrap = this.querySelector('.wac__spotlight-wrap');
      if (wrap) wrap.style.minHeight = '';
      return;
    }

    const wrap = this.querySelector('.wac__spotlight-wrap');
    if (!wrap || !this.panels.length) return;

    const width = wrap.getBoundingClientRect().width;
    if (width <= 0) return;

    let maxH = 0;

    this.panels.forEach((el) => {
      el.removeAttribute('hidden');
      el.style.visibility = 'hidden';
      el.style.position = 'absolute';
      el.style.boxSizing = 'border-box';
      el.style.width = `${width}px`;
      el.style.left = '-99999px';
      el.style.top = '0';
      maxH = Math.max(maxH, el.offsetHeight);
      el.style.visibility = '';
      el.style.position = '';
      el.style.width = '';
      el.style.left = '';
      el.style.top = '';
    });

    this.panels.forEach((el, j) => {
      if (j !== this._active) {
        el.setAttribute('hidden', '');
      } else {
        el.removeAttribute('hidden');
      }
    });

    const minPx = Math.ceil(maxH);
    wrap.style.minHeight = minPx > 0 ? `${minPx}px` : '';
  }

  disconnectedCallback() {
    this._clearTimers();
    if (this._mobileAbortController) {
      this._mobileAbortController.abort();
      this._mobileAbortController = null;
    }
    window.removeEventListener('resize', this._onResize);
    clearTimeout(this._resizeTimer);
    if (this._mobileScrollRaf != null) {
      cancelAnimationFrame(this._mobileScrollRaf);
      this._mobileScrollRaf = null;
    }
  }

  _clearTimers() {
    if (this._timer) {
      clearInterval(this._timer);
      this._timer = null;
    }
    if (this._tickTimer) {
      clearInterval(this._tickTimer);
      this._tickTimer = null;
    }
  }

  _startCycle() {
    if (this._isMobileViewport()) return;
    this._clearTimers();
    this._elapsedMs = 0;
    this._updateFillHeights();

    this._tickTimer = setInterval(() => {
      if (this._isMobileViewport()) return;
      this._elapsedMs += this._tickMs;
      if (this._elapsedMs >= this._durationMs) {
        const next = (this._active + 1) % this.items.length;
        this.select(next, false);
        this._elapsedMs = 0;
      }
      this._updateFillHeights();
    }, this._tickMs);
  }

  _updateFillHeights() {
    const pct = Math.min(100, (this._elapsedMs / this._durationMs) * 100);
    this.fills.forEach((el, i) => {
      if (i === this._active) {
        el.style.height = `${pct}%`;
      } else {
        el.style.height = '0%';
      }
    });
  }

  /**
   * @param {boolean} fromUser
   * @param {{ syncMobileScroll?: boolean }} [opts] Pass `{ syncMobileScroll: false }` when index follows scroll position.
   */
  select(index, fromUser, opts) {
    if (!this.items.length) return;
    const options = opts || {};
    const syncMobileScroll = options.syncMobileScroll !== false;
    const n = this.items.length;
    const i = ((index % n) + n) % n;
    this._active = i;

    this.items.forEach((row, j) => {
      const isOn = j === i;
      row.classList.toggle('is-active', isOn);
      row.setAttribute('aria-current', isOn ? 'true' : 'false');
    });

    this.buttons.forEach((btn, j) => {
      const isOn = j === i;
      btn.setAttribute('aria-expanded', isOn ? 'true' : 'false');
    });

    this.panels.forEach((panel, j) => {
      const isOn = j === i;
      panel.toggleAttribute('hidden', !isOn);
      panel.classList.toggle('is-active', isOn);
    });

    this.bodies.forEach((body, j) => {
      const isOn = j === i;
      if (isOn) {
        body.removeAttribute('hidden');
      } else {
        body.setAttribute('hidden', '');
      }
    });

    this._elapsedMs = 0;
    this._updateFillHeights();

    if (this._isMobileViewport() && this._mobileTrack) {
      if (syncMobileScroll) {
        this._scrollMobileToIndex(i, fromUser);
      }
      this._setMobileDotActive(i);
    }

    if (fromUser) {
      this._clearTimers();
      if (!this._reducedMotion && this.items.length > 1 && !this._isMobileViewport()) {
        this._startCycle();
      }
    }
  }
}

customElements.define('why-athletes-choose', WhyAthletesChoose);
